import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectBalance,
  selectPaymentMethod,
  updatePaymentFormPayloadItem,
} from '@cashier/cashier-data-access';
import {
  FieldControlApi,
  FormField,
  PaymentTypes,
  ValidationStatuses,
} from '@cashier/cashier-model';
import {
  encryptPreProcessedValue,
  FieldValidator,
} from '@cashier/cashier-shared';

import { useFieldRegistryContext } from '../contexts';
import { useCashierConfiguration } from './useCashierConfiguration';

interface TouchFieldContext {
  interactionCompleted: boolean;
  source?: FieldControlApi;
}

interface UseFieldToucher {
  touch: (field: FormField, context: TouchFieldContext) => void;
}

export const useFieldToucher = (): UseFieldToucher => {
  const dispatch = useDispatch();

  const { preprocessor } = useSelector(selectPaymentMethod);
  const balance = useSelector(selectBalance);

  const { paymentType } = useCashierConfiguration();
  const registry = useFieldRegistryContext();

  const touch = useCallback(
    (field: FormField, context: TouchFieldContext) => {
      const { name, value, validatorType } = field;
      const { interactionCompleted, source } = context;
      const isValidationSkipped =
        new URLSearchParams(window.location.search).get('validation') ===
        'false';

      const validationReport = isValidationSkipped
        ? { status: ValidationStatuses.SUCCESS, errors: [] }
        : FieldValidator[validatorType](field, {
            paymentType: paymentType as PaymentTypes,
            balance,
          });

      const preparedValue =
        validationReport.status === ValidationStatuses.SUCCESS
          ? encryptPreProcessedValue(value, {
              inputType: field.type,
              preprocessorType:
                field.params?.preprocessed && preprocessor
                  ? preprocessor.type
                  : undefined,
            })
          : value;

      dispatch(
        updatePaymentFormPayloadItem({
          name,
          fieldPayload: {
            value: preparedValue,
            validationStatus: validationReport.status,
            interactionCompleted,
            errors: validationReport.errors,
          },
        }),
      );

      registry?.syncControls(name, value, source);
    },
    [paymentType, balance, dispatch, preprocessor, registry],
  );

  return { touch };
};
